import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, RefreshCw, RotateCcw } from 'lucide-react';
import confetti from 'canvas-confetti';
import { sounds } from '../../utils/SoundManager';

const GRID_ROWS = 6;
const GRID_COLS = 5; // Width of one side

const SymmetryLineGame = () => {
    // Levels definition
    const levels = [
        {
            id: 1,
            name: "Nivel 1: El Triángulo",
            lines: [
                { start: 3, end: 4 },
                { start: 4, end: 9 },
                { start: 3, end: 9 }
            ]
        },
        {
            id: 2,
            name: "Nivel 2: El Rayo",
            lines: [
                { start: 0, end: 6 },
                { start: 6, end: 2 },
                { start: 2, end: 8 },
                { start: 8, end: 4 },
                { start: 0, end: 1 },
                { start: 1, end: 2 },
                { start: 2, end: 3 },
                { start: 3, end: 4 } // Top border
            ]
        },
        {
            id: 3,
            name: "Nivel 3: La Flecha",
            lines: [
                // Triangle/Arrow pointing to axis
                { start: 0, end: 6 },
                { start: 6, end: 12 },
                { start: 12, end: 18 },
                { start: 18, end: 24 },
                { start: 4, end: 8 },
                { start: 8, end: 12 },
                { start: 14, end: 18 },
                { start: 18, end: 22 },
                { start: 0, end: 1 },
                { start: 1, end: 2 },
                { start: 2, end: 3 },
                { start: 3, end: 4 },
            ]
        },
        {
            id: 4,
            name: "Nivel 4: El Diamante",
            lines: [
                { start: 4, end: 8 },
                { start: 8, end: 12 },
                { start: 12, end: 16 },
                { start: 16, end: 22 },
                { start: 22, end: 28 },
                { start: 28, end: 29 },
                // Inner
                { start: 12, end: 17 },
                { start: 17, end: 22 }
            ]
        },
        {
            id: 5,
            name: "Nivel 5: La Mariposa",
            lines: [
                // Top Wing
                { start: 4, end: 3 },
                { start: 3, end: 7 },
                { start: 7, end: 11 },
                { start: 11, end: 12 }, { start: 12, end: 13 }, { start: 13, end: 14 },

                // Bottom Wing
                { start: 14, end: 18 },
                { start: 18, end: 22 },
                { start: 22, end: 26 },
                { start: 26, end: 27 },
                { start: 27, end: 28 },
                { start: 28, end: 29 },
            ]
        },
        {
            id: 'free',
            name: "Modo Libre: ¡Crea!",
            lines: []
        }
    ];

    const [currentLevel, setCurrentLevel] = useState(0);
    const [userLines, setUserLines] = useState([]); // Array of strings "start-end"
    const [history, setHistory] = useState([]); // Undo history
    const [isSuccess, setIsSuccess] = useState(false);

    const isFreeMode = levels[currentLevel].id === 'free';

    // Calculate mirror index for the right grid
    // Left Grid: (r, c)
    // Right Grid: (r, c) mirrored.
    // Visual layout: [Left 0..4] [Axis] [Right 0..4]
    // Mirroring: Left(r, 4) mirrors Right(r, 0). Left(r, 0) mirrors Right(r, 4).
    const getPoint = (index) => {
        const r = Math.floor(index / GRID_COLS);
        const c = index % GRID_COLS;
        return { r, c };
    };

    // Calculate dynamic lines for Left Side (Reference)
    // In Free Mode, this mirrors the User Lines.
    // In Normal Mode, this is the fixed Level lines.
    const initialLines = isFreeMode
        ? userLines.map(lineKey => {
            const [start, end] = lineKey.split('-').map(Number);
            const p1 = getPoint(start);
            const p2 = getPoint(end);

            // Mirror coordinates
            const m1c = (GRID_COLS - 1) - p1.c;
            const m2c = (GRID_COLS - 1) - p2.c;

            const m1Idx = p1.r * GRID_COLS + m1c;
            const m2Idx = p2.r * GRID_COLS + m2c;

            return { start: m1Idx, end: m2Idx };
        })
        : levels[currentLevel].lines;

    const toggleLine = (p1, p2) => {
        if (isSuccess && !isFreeMode) return;

        sounds.playPop();

        // Normalize line key: smaller-larger
        const key = p1 < p2 ? `${p1}-${p2}` : `${p2}-${p1}`;

        setHistory(prev => [...prev, [...userLines]]);

        let newLines;
        if (userLines.includes(key)) {
            newLines = userLines.filter(k => k !== key);
        } else {
            newLines = [...userLines, key];
        }
        setUserLines(newLines);

        if (!isFreeMode) {
            checkSuccess(newLines);
        }
    };

    const undo = () => {
        if (history.length === 0 || (isSuccess && !isFreeMode)) return;

        sounds.playUndo();
        const previousLines = history[history.length - 1];
        setUserLines(previousLines);
        setHistory(prev => prev.slice(0, -1));

        if (isSuccess) setIsSuccess(false);
    };

    const checkSuccess = (currentLines) => {
        const targetLines = levels[currentLevel].lines;

        // Expected lines on the right side
        // Left line: {start: s, end: e}
        // Mirror Logic:
        // col' = (WIDTH - 1) - col
        const expectedKeys = targetLines.map(l => {
            const p1 = getPoint(l.start);
            const p2 = getPoint(l.end);

            const m1c = (GRID_COLS - 1) - p1.c;
            const m2c = (GRID_COLS - 1) - p2.c;

            const m1Idx = p1.r * GRID_COLS + m1c;
            const m2Idx = p2.r * GRID_COLS + m2c;

            return m1Idx < m2Idx ? `${m1Idx}-${m2Idx}` : `${m2Idx}-${m1Idx}`;
        });

        // Check if currentLines has exactly the same keys as expectedKeys
        if (currentLines.length !== expectedKeys.length) return;

        const allMatch = expectedKeys.every(k => currentLines.includes(k));

        if (allMatch) {
            setIsSuccess(true);
            sounds.playCorrect();
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 }
            });
        }
    };

    const reset = () => {
        sounds.playAction();
        setUserLines([]);
        setHistory([]);
        setIsSuccess(false);
    };

    const nextLevel = () => {
        if (currentLevel < levels.length - 1) {
            sounds.playAction();
            setCurrentLevel(prev => prev + 1);
            reset();
        }
    };

    // Rendering Helpers
    const CELL_SIZE = 70;

    const renderGridLines = (lines, color, isInteractive = false) => {
        return lines.map((l, i) => {
            // Determine p1, p2 whether input is object or string key
            let idx1, idx2;
            if (typeof l === 'string') {
                [idx1, idx2] = l.split('-').map(Number);
            } else {
                idx1 = l.start;
                idx2 = l.end;
            }

            const p1 = getPoint(idx1);
            const p2 = getPoint(idx2);

            const x1 = p1.c * CELL_SIZE + CELL_SIZE / 2;
            const y1 = p1.r * CELL_SIZE + CELL_SIZE / 2;
            const x2 = p2.c * CELL_SIZE + CELL_SIZE / 2;
            const y2 = p2.r * CELL_SIZE + CELL_SIZE / 2;

            return (
                <line
                    key={i}
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={color}
                    strokeWidth="4"
                    strokeLinecap="round"
                    className={isInteractive ? "cursor-pointer hover:stroke-brand-orange" : ""}
                    onClick={isInteractive ? () => toggleLine(idx1, idx2) : undefined}
                />
            );
        });
    };

    // Generate all potential segments for interactive grid to allow clicking "ghosts"
    const potentialSegments = [];
    if (!isSuccess) {
        for (let r = 0; r < GRID_ROWS; r++) {
            for (let c = 0; c < GRID_COLS; c++) {
                const idx = r * GRID_COLS + c;
                // Right neighbor
                if (c < GRID_COLS - 1) potentialSegments.push({ start: idx, end: idx + 1 });
                // Bottom neighbor
                if (r < GRID_ROWS - 1) potentialSegments.push({ start: idx, end: idx + GRID_COLS });
                // Diagonal neighbor (bottom-right)
                if (r < GRID_ROWS - 1 && c < GRID_COLS - 1) potentialSegments.push({ start: idx, end: idx + GRID_COLS + 1 });
                // Diagonal neighbor (bottom-left)
                if (r < GRID_ROWS - 1 && c > 0) potentialSegments.push({ start: idx, end: idx + GRID_COLS - 1 });
            }
        }
    }

    return (
        <div className="flex flex-col md:flex-row gap-8 items-start w-full">
            {/* Sidebar Level Selector */}
            <div className="flex flex-row md:flex-col gap-4 p-4 bg-white rounded-2xl shadow-lg overflow-x-auto md:overflow-visible w-full md:w-auto min-w-[150px] scrollbar-hide">
                <h3 className="hidden md:block text-lg font-bold text-gray-400 mb-2 text-center">Niveles</h3>
                {levels.map((level, idx) => (
                    <button
                        key={level.id}
                        onClick={() => {
                            sounds.playAction();
                            setCurrentLevel(idx);
                            reset();
                        }}
                        className={`
                            relative flex flex-col items-center gap-2 p-2 rounded-xl transition-all min-w-[80px]
                            ${idx === currentLevel ? 'bg-brand-purple/10 border-2 border-brand-purple' : 'hover:bg-gray-50 border-2 border-transparent'}
                        `}
                    >
                        {/* Mini SVG Thumbnail */}
                        <div className="w-12 h-14 pointer-events-none opacity-80">
                            <svg width="100%" height="100%" viewBox={`0 0 ${GRID_COLS * 40} ${GRID_ROWS * 40}`}>
                                {/* Just render the pattern lines for preview */}
                                {level.lines.map((l, i) => {
                                    let idx1, idx2;
                                    if (typeof l === 'string') { [idx1, idx2] = l.split('-').map(Number); }
                                    else { idx1 = l.start; idx2 = l.end; }
                                    const p1 = getPoint(idx1);
                                    const p2 = getPoint(idx2);
                                    return (
                                        <line key={i}
                                            x1={p1.c * 40 + 20} y1={p1.r * 40 + 20}
                                            x2={p2.c * 40 + 20} y2={p2.r * 40 + 20}
                                            stroke="#3b82f6" strokeWidth="12" strokeLinecap="round"
                                        />
                                    );
                                })}
                            </svg>
                        </div>

                        <span className={`text-xs font-bold whitespace-nowrap ${idx === currentLevel ? 'text-brand-purple' : 'text-gray-400'}`}>
                            {level.name.split(':')[1].trim()}
                        </span>
                    </button>
                ))}
            </div>

            {/* Main Game Area */}
            <div className="flex-1 flex flex-col items-center gap-8 w-full">
                <div className="flex flex-col items-center gap-2">
                    <div className="text-xl font-bold text-brand-purple">
                        {levels[currentLevel].name}
                    </div>
                </div>

                <div className="flex items-start justify-center p-4 bg-white rounded-3xl shadow-lg border-2 border-gray-100">
                    {/* Left Side */}
                    <div className="relative bg-blue-50 border-r-4 border-red-500 rounded-l-xl"
                        style={{ width: GRID_COLS * CELL_SIZE, height: GRID_ROWS * CELL_SIZE }}>
                        <svg width="100%" height="100%">
                            {/* Dots */}
                            {Array(GRID_ROWS * GRID_COLS).fill(0).map((_, i) => {
                                const { r, c } = getPoint(i);
                                return <circle key={i} cx={c * CELL_SIZE + CELL_SIZE / 2} cy={r * CELL_SIZE + CELL_SIZE / 2} r="3" fill="#cbd5e1" />
                            })}
                            {/* Lines */}
                            {renderGridLines(initialLines, "#3b82f6")}
                        </svg>
                    </div>

                    {/* Right Side */}
                    <div className="relative bg-white rounded-r-xl"
                        style={{ width: GRID_COLS * CELL_SIZE, height: GRID_ROWS * CELL_SIZE }}>
                        <svg width="100%" height="100%">
                            {/* Ghost Lines for interaction */}
                            {!isSuccess && renderGridLines(potentialSegments, "transparent", true).map((el, i) => React.cloneElement(el, {
                                strokeWidth: 15, // Hit area
                                className: "cursor-pointer hover:stroke-gray-200 transition-colors"
                            }))}

                            {/* Dots */}
                            {Array(GRID_ROWS * GRID_COLS).fill(0).map((_, i) => {
                                const { r, c } = getPoint(i);
                                return <circle key={i} cx={c * CELL_SIZE + CELL_SIZE / 2} cy={r * CELL_SIZE + CELL_SIZE / 2} r="3" fill="#cbd5e1" />
                            })}

                            {/* Active Lines */}
                            {renderGridLines(userLines, "#f97316")}
                        </svg>
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={undo}
                        disabled={history.length === 0 || isSuccess}
                        className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-colors ${history.length === 0 || isSuccess ? 'bg-gray-100 text-gray-400' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}
                    >
                        <RotateCcw size={20} /> Deshacer
                    </button>
                    <button
                        onClick={reset}
                        className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-full font-bold hover:bg-gray-300 transition-colors"
                    >
                        <RefreshCw size={20} /> Borrar Todo
                    </button>
                    {isSuccess && (
                        <>
                            {currentLevel < levels.length - 1 ? (
                                <button
                                    onClick={nextLevel}
                                    className="flex items-center gap-2 px-6 py-3 bg-brand-green text-white rounded-full font-bold animate-pulse shadow-lg hover:scale-105 transition-transform"
                                >
                                    <Check size={24} /> Siguiente Nivel
                                </button>
                            ) : (
                                <div className="flex items-center gap-2 px-6 py-3 bg-brand-yellow text-white rounded-full font-bold animate-bounce shadow-lg">
                                    🏆 ¡Campeón!
                                </div>
                            )}
                        </>
                    )}
                </div>
                <p className="text-gray-400 text-sm">Toca entre dos puntos para dibujar una línea.</p>
            </div>
        </div>
    );
};
export default SymmetryLineGame;
